import {
  LoadingPage,
  SummaryPage,
} from '@resurs-internal/deposits-app-components';
import { useContext, useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router';

import { type ApplicationHandlerProps } from '@/components';
import { CountryContext, SectionContext } from '@/contexts';
import {
  getSectionBeforeSummary,
  getSectionCount,
  hasFixedTermView,
  isAuthenticated,
  sendGTMApplicationProgress,
  sendGTMFormInteraction,
} from '@/helpers';
import { useApplication } from '@/hooks';
import { fetchDocument, lookupBankName } from '@/services';

export const SummaryView = () => {
  const { applicationId, product } =
    useOutletContext() as ApplicationHandlerProps;
  const { application, isLoading, links, submit } =
    useApplication(applicationId);
  const { country } = useContext(CountryContext);
  const { setNavigationDetails } = useContext(SectionContext);
  const navigate = useNavigate();

  const [displayError, setDisplayError] = useState<boolean>(false);
  const [isValid, setValid] = useState<boolean>(false);
  const section = useMemo(
    () =>
      getSectionCount(
        country,
        application.productType,
        hasFixedTermView(product),
      ),
    [country, application.productType, product],
  );

  const handleGTMProgress = (
    applicationId: string,
    productVariant: string,
    stepName: string,
    stepNumber: number,
  ) => {
    sendGTMApplicationProgress({
      applicationId,
      country,
      productVariant,
      stepName,
      stepNumber,
    });
  };
  const handleGTMFormInteraction = (
    formName: string,
    productVariant: string,
  ) => {
    sendGTMFormInteraction(formName, productVariant, country);
  };

  if (isLoading) {
    return <LoadingPage country={country} />;
  }

  const handleNavigateBack = () => {
    setNavigationDetails({
      part: 0,
      section: getSectionBeforeSummary(country, application.productType),
      subSection: 0,
    });
    navigate(-1);
  };

  return (
    <SummaryPage
      applicationId={applicationId}
      contactInfo={application.contactInfo}
      country={country}
      disbursementAccount={application.disbursementAccount}
      displayError={displayError}
      fetchDocument={fetchDocument}
      interestRate={application.interestRate}
      isAuthenticated={isAuthenticated()}
      isLoading={isLoading}
      isValid={isValid}
      links={links}
      lookupBankName={lookupBankName}
      navigateBack={handleNavigateBack}
      product={product}
      section={section - 1}
      sendGTMApplicationProgress={handleGTMProgress}
      sendGTMFormInteraction={handleGTMFormInteraction}
      setDisplayError={setDisplayError}
      setValid={setValid}
      submit={submit}
      term={application.term}
      totalSections={section}
    />
  );
};
